"use client";

import * as React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/overlays/popover";
import { CalendarIcon } from "lucide-react";
import type { Matcher } from "react-day-picker";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/primitives/button";
import { useDateTimeInputDate } from "@/hooks/useDateTimeInputDate";
import { SIZE_CLASSES, type DateTimeInputSize } from "@/components/ui/utils/date-time-input-utils";
import { TimePickerPanel } from "@/components/ui/forms/time-picker-panel";

const TIME_PATTERN = /^([01]\d|2[0-3]):([0-5]\d)$/;

function normalizeTimeInput(raw: string): string | null {
  const trimmed = raw.trim();
  if (!trimmed) return null;
  if (TIME_PATTERN.test(trimmed)) return trimmed;
  return null;
}

export interface DateTimeInputProps
  extends Omit<React.ComponentProps<"input">, "size" | "type"> {
  /** Input type: "date" or "time" */
  variant: "date" | "time";
  /** Label text displayed above the input */
  label?: string;
  /** Helper text displayed below the input */
  helperText?: string;
  /** When true, shows "(optional)" after the label. Required is the default. */
  optional?: boolean;
  /** When true, displays error styling */
  isError?: boolean;
  /** Error message that replaces helperText when isError is true */
  errorMessage?: string;
  /** Size variant of the input */
  size?: "sm" | "md" | "lg";
  /** Additional class name for the wrapper element */
  wrapperClassName?: string;
}

const DateTimeInput = React.forwardRef<HTMLInputElement, DateTimeInputProps>(
  (
    {
      className,
      variant,
      label,
      helperText,
      optional = false,
      isError = false,
      errorMessage,
      size = "md",
      disabled,
      id,
      wrapperClassName,
      onChange,
      max,
      min,
      value,
      placeholder,
      ...props
    },
    ref
  ) => {
    const internalRef = React.useRef<HTMLInputElement>(null);
    const inputRef = (ref as React.RefObject<HTMLInputElement>) || internalRef;
    const { t } = useTranslation("common");

    const generatedId = React.useId();
    const inputId = id ?? generatedId;
    const helperId = `${inputId}-helper`;
    const hasHelperOrError = !!(helperText || (isError && errorMessage));

    const currentSize = SIZE_CLASSES[size as DateTimeInputSize];
    const paddingClasses = cn(
      currentSize.paddingL,
      currentSize.paddingRightWithIcon
    );
    const resolvedMax = variant === "date" ? (max ?? "9999-12-31") : max;

    const iconClass = variant === "date" ? "pi-calendar" : "pi-clock";

    const dateState = useDateTimeInputDate({
      variant,
      value,
      max: typeof resolvedMax === "string" ? resolvedMax : undefined,
      min: typeof min === "string" ? min : undefined,
      onChange,
      t,
      isError,
      errorMessage,
      helperText,
    });

    // Stable identity, so an unrelated parent re-render does not make react-day-picker
    // re-match every visible day while the popover is open.
    const { minDate, maxDate } = dateState;
    const disabledDays = React.useMemo<Matcher[]>(
      () => [
        ...(minDate ? [{ before: minDate }] : []),
        ...(maxDate ? [{ after: maxDate }] : []),
      ],
      [minDate, maxDate]
    );

    const [timeOpen, setTimeOpen] = React.useState(false);
    const [timeDraft, setTimeDraft] = React.useState<string | undefined>(undefined);

    if (variant === "date") {
      return (
        <div className={cn("flex flex-col gap-1", wrapperClassName)}>
          {label && (
            <label
              htmlFor={inputId}
              className={cn(
                "body-sm-medium text-(--input-label) flex flex-nowrap truncate",
                disabled && "opacity-50"
              )}
            >
              {label}
              {optional && (
                <span className="text-(--input-label-optional) body-sm-regular ml-1">
                  (optional)
                </span>
              )}
            </label>
          )}

          <div className="relative flex items-center">
            <input
              ref={inputRef}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              id={inputId}
              disabled={disabled}
              value={dateState.displayValue}
              placeholder={placeholder ?? "dd-mm-yyyy"}
              aria-invalid={dateState.dateIsError}
              aria-describedby={dateState.dateHelperOrError ? helperId : undefined}
              onChange={dateState.handleDateInputChange}
              onBlur={dateState.handleDateInputBlur}
              onFocus={dateState.handleDateInputFocus}
              className={cn(
                "w-full rounded-(--input-radius) border transition-colors",
                "shadow-(--input-shadow)",
                "outline-1 -outline-offset-1 outline-transparent",
                "h-[38px]",
                currentSize.input,
                paddingClasses,
                "bg-(--input-bg) border-(--input-border) text-(--input-text)",
                dateState.dateIsError
                  ? "placeholder:text-(--input-text-placeholder-error)"
                  : "placeholder:text-(--input-text-placeholder)",
                !dateState.dateIsError && !disabled && "hover:border-(--input-border-hover)",
                !dateState.dateIsError &&
                  "focus:outline-[1.5px] focus:-outline-offset-[1.5px] focus:outline-(--input-border-focus)",
                dateState.dateIsError && "border-(--input-border-error)",
                dateState.dateIsError &&
                  "focus:outline-[1.5px] focus:-outline-offset-[1.5px] focus:outline-(--input-border-error)",
                disabled && [
                  "bg-(--input-bg-disabled)",
                  "text-(--input-text-disabled)",
                  "cursor-not-allowed",
                  "pointer-events-none",
                ],
                className
              )}
            />
            <Popover open={dateState.open} onOpenChange={dateState.setOpen}>
              <PopoverTrigger asChild>
                <button
                  type="button"
                  disabled={disabled}
                  aria-label="Open calendar"
                  className={cn(
                    "absolute top-1/2 -translate-y-1/2 flex items-center justify-center cursor-pointer",
                    currentSize.iconRight,
                    currentSize.icon,
                    dateState.dateIsError ? "text-(--input-icon-error)" : "text-text-tertiary",
                    "hover:text-(--input-icon-hover) transition-colors",
                    disabled && "opacity-50 cursor-not-allowed pointer-events-none"
                  )}
                >
                  <CalendarIcon className="size-4" aria-hidden />
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-auto overflow-hidden p-0 bg-white" align="end">
                <Calendar
                  mode="single"
                  selected={dateState.selectedDate}
                  defaultMonth={dateState.selectedDate}
                  captionLayout="dropdown"
                  startMonth={new Date(2020, 0)}
                  endMonth={new Date(2040, 11)}
                  disabled={disabledDays}
                  onSelect={dateState.handleSelect}
                />
                <div className="flex gap-2 p-2 border-t border-border-secondary bg-white">
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="flex-1"
                    onClick={dateState.handleClear}
                  >
                    {t("clear")}
                  </Button>
                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    className="flex-1"
                    onClick={dateState.handleToday}
                    disabled={dateState.isTodayDisabled}
                  >
                    {t("today")}
                  </Button>
                </div>
              </PopoverContent>
            </Popover>
          </div>

          {dateState.dateHelperOrError && (
            <p
              id={helperId}
              className={cn(
                "body-xs-regular",
                dateState.dateIsError ? "text-(--input-helper-error)" : "text-(--input-helper)",
                disabled && "opacity-50"
              )}
            >
              {dateState.dateHelperMessage}
            </p>
          )}
        </div>
      );
    }

    // Time variant: custom popover picker (avoids native Chrome picker layout gaps)
    const timeDisplayValue =
      timeDraft !== undefined ? timeDraft : typeof value === "string" ? value : "";

    const emitTimeChange = (nextTime: string) => {
      onChange?.({
        target: { value: nextTime },
      } as React.ChangeEvent<HTMLInputElement>);
    };

    const handleTimeInputChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
      setTimeDraft(e.target.value);
    };

    const handleTimeInputBlur = () => {
      if (timeDraft === undefined) return;
      const normalized = normalizeTimeInput(timeDraft);
      if (normalized) {
        emitTimeChange(normalized);
      }
      setTimeDraft(undefined);
    };

    const handleTimePickerChange = (nextTime: string) => {
      setTimeDraft(undefined);
      emitTimeChange(nextTime);
    };

    return (
      <div className={cn("flex w-full flex-col gap-1", wrapperClassName)}>
        {label && (
          <label
            htmlFor={inputId}
            className={cn(
              "body-sm-medium text-(--input-label) flex flex-nowrap truncate",
              disabled && "opacity-50"
            )}
          >
            {label}
            {optional && (
              <span className="text-(--input-label-optional) body-sm-regular ml-1">
                (optional)
              </span>
            )}
          </label>
        )}

        <Popover open={timeOpen} onOpenChange={setTimeOpen}>
          <div className="relative flex w-full items-center">
            <input
              ref={inputRef}
              id={inputId}
              type="text"
              inputMode="numeric"
              autoComplete="off"
              disabled={disabled}
              value={timeDisplayValue}
              placeholder={placeholder ?? "HH:mm"}
              aria-invalid={isError}
              aria-describedby={hasHelperOrError ? helperId : undefined}
              className={cn(
                "w-full rounded-(--input-radius) border transition-colors",
                "shadow-(--input-shadow)",
                "outline-1 -outline-offset-1 outline-transparent",
                "h-[38px]",
                currentSize.input,
                paddingClasses,
                "bg-(--input-bg) border-(--input-border) text-(--input-text)",
                isError
                  ? "placeholder:text-(--input-text-placeholder-error)"
                  : "placeholder:text-(--input-text-placeholder)",
                !isError && !disabled && "hover:border-(--input-border-hover)",
                !isError &&
                  "focus:outline-[1.5px] focus:-outline-offset-[1.5px] focus:outline-(--input-border-focus)",
                isError && "border-(--input-border-error)",
                isError &&
                  "focus:outline-[1.5px] focus:-outline-offset-[1.5px] focus:outline-(--input-border-error)",
                disabled && [
                  "bg-(--input-bg-disabled)",
                  "text-(--input-text-disabled)",
                  "cursor-not-allowed",
                  "pointer-events-none",
                ],
                className
              )}
              onChange={handleTimeInputChange}
              onBlur={handleTimeInputBlur}
              max={resolvedMax}
              {...props}
            />
            <PopoverTrigger asChild>
              <button
                type="button"
                disabled={disabled}
                aria-label="Open time picker"
                className={cn(
                  "absolute top-1/2 -translate-y-1/2 flex items-center justify-center cursor-pointer",
                  currentSize.iconRight,
                  currentSize.icon,
                  isError ? "text-(--input-icon-error)" : "text-(--input-icon)",
                  "hover:text-(--input-icon-hover) transition-colors",
                  disabled && "opacity-50 cursor-not-allowed pointer-events-none"
                )}
              >
                <i className={cn("pi", iconClass)} aria-hidden="true" />
              </button>
            </PopoverTrigger>
            <PopoverContent
              className="w-fit overflow-hidden p-0 bg-white"
              align="end"
            >
              <TimePickerPanel
                value={typeof value === "string" ? value : ""}
                onChange={handleTimePickerChange}
              />
            </PopoverContent>
          </div>
        </Popover>

        {hasHelperOrError && (
          <p
            id={helperId}
            className={cn(
              "body-xs-regular",
              isError ? "text-(--input-helper-error)" : "text-(--input-helper)",
              disabled && "opacity-50"
            )}
          >
            {isError && errorMessage ? errorMessage : helperText}
          </p>
        )}
      </div>
    );
  }
);

DateTimeInput.displayName = "DateTimeInput";

export { DateTimeInput };

